import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  collection,
  getDocs,
  orderBy,
  query,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import AutoFillProgramService from "../services/autoFill/AutoFillProgramService";
import {
  adminVerifyProgramWalletForWithdrawal,
  adminVerifyVBoostForWithdrawal,
} from "../services/autoFill/autoFillWithdrawalUserActions";

const AutoFillWithdrawalManagement = ({ adminUserId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, row: null });
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, "autoFillWithdrawals"), orderBy("createdAt", "desc"))
      );
      const loaded = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRows(loaded);

      const completed = loaded.filter((r) => r.status === "COMPLETED");
      for (const row of completed) {
        try {
          const result = await AutoFillProgramService.creditPointsWalletForWithdrawal(
            row
          );
          if (result.credited) {
            console.log(
              `Credited ₹${row.amount} to Wallet & Points for ${row.userEmail}`
            );
          }
        } catch (syncErr) {
          console.error("Auto Fill wallet sync failed:", syncErr);
          setError(
            `Wallet credit sync failed for ${row.userEmail}: ${syncErr.message}`
          );
        }
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const approve = async (row) => {
    setProcessing(row.id);
    try {
      await AutoFillProgramService.onWithdrawalApproved(row, adminUserId || "admin");
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(null);
    }
  };

  const reject = async () => {
    if (!rejectDialog.row) return;
    await AutoFillProgramService.onWithdrawalRejected(rejectDialog.row);
    await updateDoc(doc(db, "autoFillWithdrawals", rejectDialog.row.id), {
      status: "REJECTED",
      rejectionReason: rejectReason,
      rejectedAt: new Date().toISOString(),
    });
    setRejectDialog({ open: false, row: null });
    setRejectReason("");
    load();
  };

  if (loading) return <CircularProgress />;

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Auto Fill — Points withdrawals
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.withdrawalId}</TableCell>
                <TableCell>
                  {row.userName}
                  <br />
                  <Typography variant="caption">{row.userEmail}</Typography>
                </TableCell>
                <TableCell>₹{row.amount}</TableCell>
                <TableCell>
                  <Chip size="small" label={row.status} />
                </TableCell>
                <TableCell>
                  {row.status === "PENDING" && (
                    <>
                      <Button
                        size="small"
                        onClick={() => approve(row)}
                        disabled={processing === row.id}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setRejectDialog({ open: true, row })}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false, row: null })}>
        <DialogTitle>Reject withdrawal</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            label="Reason"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog({ open: false, row: null })}>Cancel</Button>
          <Button color="error" onClick={reject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AutoFillWithdrawalManagement;
